import { Box, Chip, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, PrintOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { type MouseEvent, useCallback, useMemo } from "react"

import { GoodsReceiptRowActions } from "../components"
import { GOODS_RECEIPT_DESKTOP_COLUMNS_CONFIG, GOODS_RECEIPT_TABLET_COLUMNS_CONFIG } from "../constants"
import type { GoodsReceiptColumnKey } from "../types"
import {
  formatPalletCount,
  getGoodsReceiptContainerTrackingNumbers,
  resolveGoodsReceiptPalletsCount,
  resolveGoodsReceiptPalletsCountFromPalletRows,
  resolveGoodsReceiptSupplierNames,
} from "../utils"

import { ValueOrMultiple } from "@/components/common/ValueOrMultiple"
import { PermissionActionType } from "@/constants/permissions"
import { HOLD_AREA_1 } from "@/constants/warehouse"
import { type GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { formatDate } from "@/utils/date"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface UseGoodsReceiptColumnsProps {
  status: GoodsReceiptStatus
  onEdit?: (goodsReceipt: GoodsReceipt) => void
  onDelete?: (goodsReceipt: GoodsReceipt) => void
  onPrint?: (goodsReceipt: GoodsReceipt) => void
  onMarkAsNotReceived?: (goodsReceipt: GoodsReceipt) => void
}

export const useGoodsReceiptColumns = ({
  status,
  onEdit,
  onDelete,
  onPrint,
  onMarkAsNotReceived,
}: UseGoodsReceiptColumnsProps): ColumnDef<GoodsReceipt>[] => {
  const { isTabletView } = useAppView()
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const handleStopPropagation = useCallback((event: MouseEvent<HTMLElement>) => event.stopPropagation(), [])

  // Define all possible column definitions
  const allColumnDefinitions: Record<GoodsReceiptColumnKey, ColumnDef<GoodsReceipt>> = useMemo(
    () => ({
      agrNumber: {
        accessorKey: "agrNumber",
        header: "AGR #",
        enableSorting: true,
        sortingFn: "alphanumeric",
        cell: ({ row }) => {
          const palletsCount = resolveGoodsReceiptPalletsCount(row.original)

          return (
            <Box component='span' className='flex flex-col gap-y-1'>
              <Box component='span' className='font-bold text-primary'>
                {row.original.agrNumber}
              </Box>
              <Box component='span'>{formatPalletCount(palletsCount)}</Box>
            </Box>
          )
        },
      },
      grNumber: {
        accessorKey: "agrNumber",
        header: "GR #",
        enableSorting: true,
        sortingFn: "alphanumeric",
        cell: ({ row }) => {
          const palletsCount =
            status === GoodsReceiptStatus.CheckedIn
              ? resolveGoodsReceiptPalletsCountFromPalletRows(row.original.palletRows ?? [])
              : resolveGoodsReceiptPalletsCount(row.original)

          return (
            <Box component='span' className='flex flex-col gap-y-1'>
              <Box component='span' className='font-bold text-primary'>
                {row.original.agrNumber}
              </Box>
              <Box component='span'>{formatPalletCount(palletsCount)}</Box>
            </Box>
          )
        },
      },
      expectedDate: {
        accessorKey: "estimatedDeliveryDate",
        header: "Expected",
        enableSorting: true,
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <Chip
            className='h-6 rounded-sm bg-primary-200 text-xs'
            label={
              <>
                <Box component='span' className='font-bold'>
                  By
                </Box>
                &nbsp;
                {formatDate(row.original.estimatedDeliveryOn)}
              </>
            }
          />
        ),
      },
      softReceivedOn: {
        accessorKey: "softReceivedOn",
        header: "Soft Received",
        enableSorting: true,
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <Chip className='h-6 rounded-sm bg-primary-200 text-xs' label={formatDate(row.original.softReceivedOn)} />
        ),
      },
      checkedInOn: {
        accessorKey: "checkedInOn",
        header: "Checked In",
        enableSorting: true,
        sortingFn: "alphanumeric",
        cell: ({ row }) => (
          <Chip className='h-6 rounded-sm bg-primary-200 text-xs' label={formatDate(row.original.checkedInOn)} />
        ),
      },
      trackingNumbers: {
        accessorKey: "trackingNumbers",
        header: isTabletView ? "Tracking #" : "Container/Tracking #",
        enableSorting: false,
        cell: ({ row }) => <ValueOrMultiple values={getGoodsReceiptContainerTrackingNumbers(row.original)} />,
      },
      supplierName: {
        accessorKey: "supplierName",
        header: "Supplier",
        enableSorting: true,
        sortingFn: "alphanumeric",
        cell: ({ row }) => <ValueOrMultiple values={resolveGoodsReceiptSupplierNames(row.original)} />,
      },
      holdLocation: {
        accessorKey: "holdLocation",
        header: "Hold Location",
        enableSorting: false,
        cell: () => HOLD_AREA_1,
      },
      warehouse: {
        accessorKey: "warehouse",
        header: "Warehouse",
        enableSorting: true,
        sortingFn: "alphanumeric",
        cell: ({ row }) => (row.original.warehouseArea ? formatWarehouseLabel(row.original.warehouseArea) : ""),
      },
      actions: {
        accessorKey: "actions",
        header: isTabletView ? "QR" : "",
        enableSorting: false,
        size: 64,
        cell: ({ row }) =>
          isTabletView ? (
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation()
                onPrint?.(row.original)
              }}
            >
              <PrintOutlined />
            </IconButton>
          ) : (
            <Box component='span' className='flex items-center justify-end gap-x-3' onClick={handleStopPropagation}>
              {canMaintain && (
                <GoodsReceiptRowActions
                  row={row}
                  status={status}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onMarkAsNotReceived={onMarkAsNotReceived}
                />
              )}
              <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
                {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
              </IconButton>
            </Box>
          ),
      },
    }),
    [onEdit, onDelete, onMarkAsNotReceived, onPrint, handleStopPropagation, status, canMaintain, isTabletView]
  )

  // Get the column keys for the current status and build the columns array
  return useMemo(() => {
    const effectiveStatus = status === GoodsReceiptStatus.PreCheckedIn ? GoodsReceiptStatus.SoftReceived : status

    const columnKeys = isTabletView
      ? GOODS_RECEIPT_TABLET_COLUMNS_CONFIG[
          effectiveStatus as Exclude<GoodsReceiptStatus, GoodsReceiptStatus.PreCheckedIn>
        ]
      : GOODS_RECEIPT_DESKTOP_COLUMNS_CONFIG[
          effectiveStatus as Exclude<GoodsReceiptStatus, GoodsReceiptStatus.PreCheckedIn>
        ]

    return columnKeys.map((key: GoodsReceiptColumnKey) => allColumnDefinitions[key])
  }, [status, allColumnDefinitions, isTabletView])
}
